<script setup lang="ts">
import type { Component } from 'vue';

interface SheetSectionOption {
  value: string;
  label: string;
  icon?: Component;
}

defineProps<{
  modelValue: string;
  options: readonly SheetSectionOption[];
  ariaLabel?: string;
  placement?: 'side' | 'bottom';
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void;
}>();
</script>

<template>
  <nav
    :aria-label="ariaLabel || 'Sheet sections'"
    :class="placement === 'bottom'
      ? 'min-w-0 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden'
      : 'hidden xl:sticky xl:top-5 xl:block'"
  >
    <div
      :class="placement === 'bottom'
        ? 'flex min-w-max items-center gap-1 px-4 py-2 sm:px-6'
        : 'grid grid-cols-2 gap-2 border-border/70 xl:grid-cols-1 xl:border-l xl:pl-4'"
    >
      <button
        v-for="option in options"
        :key="option.value"
        type="button"
        class="flex items-center gap-2 border border-transparent text-left text-muted-foreground transition-colors"
        :class="[
          placement === 'bottom'
            ? 'h-9 shrink-0 rounded-md px-3'
            : 'gap-3 rounded-2xl px-3 py-3 xl:flex-col xl:justify-center xl:gap-4 xl:px-2 xl:py-5 xl:text-center',
          modelValue === option.value
            ? (placement === 'bottom'
                ? 'border-border bg-muted text-foreground'
                : 'bg-primary/10 text-foreground shadow-[inset_3px_0_0_var(--brand-primary)] xl:shadow-[inset_2px_0_0_var(--brand-primary)]')
            : 'hover:border-border/60 hover:bg-muted/60 hover:text-foreground',
        ]"
        @click="emit('update:modelValue', option.value)"
      >
        <component :is="option.icon" v-if="option.icon" class="size-4 shrink-0" />
        <span class="text-sm font-medium">{{ option.label }}</span>
      </button>
    </div>
  </nav>
</template>
